/**
 * Lógica pura do formulário de nova versão comercial (E51.F4). // ux-neutrality:allow doc-comment
 *
 * Espelha a validação do contrato Pydantic no client: vigência coerente, faixas // ux-neutrality:allow doc-comment
 * contínuas a partir do volume 1 (o início de cada faixa é derivado do fim da // ux-neutrality:allow doc-comment
 * anterior), última aberta e certificação agregada >= exame. Sem React, para ser // ux-neutrality:allow doc-comment
 * testável em unidade. O backend continua sendo a autoridade da validação. // ux-neutrality:allow doc-comment
 */
import type { FaixaInput } from '@/lib/precificacao';

export interface FaixaRow {
  volumeFim: string;
  exameReais: string;
  certificacaoReais: string;
}

export interface FormState {
  vigenciaInicio: string;
  vigenciaFim: string;
  faixas: FaixaRow[];
}

export type ValidationKey =
  | 'validationVigenciaObrigatoria'
  | 'validationVigencia'
  | 'validationFaixaVazia'
  | 'validationParticao'
  | 'validationUltimaAberta'
  | 'validationAgregado'
  | 'validationValores';

export interface BuiltVersao {
  vigencia_inicio: string;
  vigencia_fim: string;
  faixas: FaixaInput[];
}

export type ValidationResult = { ok: true; built: BuiltVersao } | { ok: false; key: ValidationKey };

export function emptyFaixaRow(): FaixaRow {
  return { volumeFim: '', exameReais: '', certificacaoReais: '' };
}

function reaisParaCentavos(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const numero = Number(trimmed);
  if (!Number.isFinite(numero) || numero < 0) return null;
  return Math.round(numero * 100);
}

/** Início de cada faixa derivado do fim da anterior (1-based); `null` se indeterminado. */
export function derivarInicios(faixas: FaixaRow[]): (number | null)[] {
  const inicios: (number | null)[] = [];
  let atual: number | null = 1;
  for (const row of faixas) {
    inicios.push(atual);
    const fim = Number(row.volumeFim);
    atual =
      atual !== null && row.volumeFim.trim() !== '' && Number.isInteger(fim) && fim >= atual
        ? fim + 1
        : null;
  }
  return inicios;
}

function _volumeFimDaFaixa(
  row: FaixaRow,
  ultima: boolean,
  inicio: number,
): { key: ValidationKey } | { volumeFim: number | null } {
  if (ultima) {
    return row.volumeFim.trim() === '' ? { volumeFim: null } : { key: 'validationUltimaAberta' };
  }
  const fim = Number(row.volumeFim);
  if (row.volumeFim.trim() === '' || !Number.isInteger(fim) || fim < inicio) {
    return { key: 'validationParticao' };
  }
  return { volumeFim: fim };
}

function _validarFaixa(
  row: FaixaRow,
  ultima: boolean,
  inicio: number,
  ordem: number,
): { key: ValidationKey } | { faixa: FaixaInput } {
  const exame = reaisParaCentavos(row.exameReais);
  const cert = reaisParaCentavos(row.certificacaoReais);
  if (exame === null || cert === null) {
    return { key: 'validationValores' };
  }
  if (cert < exame) {
    return { key: 'validationAgregado' };
  }
  const limite = _volumeFimDaFaixa(row, ultima, inicio);
  if ('key' in limite) {
    return { key: limite.key };
  }
  return {
    faixa: {
      ordem,
      volume_inicio: inicio,
      volume_fim: limite.volumeFim,
      valor_exame_centavos: exame,
      valor_certificacao_agregado_centavos: cert,
    },
  };
}

export function validarNovaVersao(state: FormState): ValidationResult {
  if (state.vigenciaInicio === '' || state.vigenciaFim === '') {
    return { ok: false, key: 'validationVigenciaObrigatoria' };
  }
  if (state.vigenciaFim < state.vigenciaInicio) {
    return { ok: false, key: 'validationVigencia' };
  }
  if (state.faixas.length === 0) {
    return { ok: false, key: 'validationFaixaVazia' };
  }
  const faixas: FaixaInput[] = [];
  let inicio = 1;
  for (const [i, row] of state.faixas.entries()) {
    const ultima = i === state.faixas.length - 1;
    const parsed = _validarFaixa(row, ultima, inicio, i + 1);
    if ('key' in parsed) {
      return { ok: false, key: parsed.key };
    }
    faixas.push(parsed.faixa);
    if (parsed.faixa.volume_fim !== null) {
      inicio = parsed.faixa.volume_fim + 1;
    }
  }
  return {
    ok: true,
    built: {
      vigencia_inicio: state.vigenciaInicio,
      vigencia_fim: state.vigenciaFim,
      faixas,
    },
  };
}
